import { AnalysisEventPoint } from "../../api/analysis";

/** 서버 집계값(숫자·문자 섞임)을 숫자로 */
export const n = (v: unknown): number => {
  const x = typeof v === "number" ? v : Number(v);
  return Number.isFinite(x) ? x : 0;
};

export const fmtInt = (v: unknown) => Math.round(n(v)).toLocaleString();

export const fmt1 = (v: unknown) =>
  n(v).toLocaleString(undefined, { maximumFractionDigits: 1 });

export const fmtPct = (num: unknown, den: unknown, digits = 1) => {
  const d = n(den);
  if (d <= 0) return "-";
  return `${((n(num) / d) * 100).toFixed(digits)}%`;
};

/** 분 → "N시간 M분" */
export const fmtMinutes = (v: unknown) => {
  const m = Math.round(n(v));
  if (!m) return "-";
  const h = Math.floor(m / 60);
  return h ? `${h}시간 ${m % 60}분` : `${m}분`;
};

/** 1만 운행시간당 건수 */
export const per10kHours = (count: unknown, hours: unknown) => {
  const h = n(hours);
  return h > 0 ? (n(count) / h) * 10000 : 0;
};

/** 전기 대비 증감률. 전기가 0 이면 비교하지 않는다 */
export const delta = (cur: unknown, prev: unknown): number | null => {
  const p = n(prev);
  if (p <= 0) return null;
  return ((n(cur) - p) / p) * 100;
};

export interface EventTypeMeta {
  label: string;
  color: string;
  group: "rollover" | "danger" | "area" | "etc";
}

/** 이벤트 유형 (tb_cd ocrn_type_cd) */
export const EVENT_TYPES: Record<string, EventTypeMeta> = {
  "2": { label: "차량전복", color: "#d7263d", group: "rollover" },
  "3": { label: "위험운전", color: "#f28c28", group: "danger" },
  "6": { label: "사용지역이탈", color: "#7c3aed", group: "area" },
  "9": { label: "위험지역진입", color: "#2563eb", group: "area" },
  "5": { label: "시동방치", color: "#6b7280", group: "etc" },
  "7": { label: "도난감지", color: "#111827", group: "etc" },
};

export const EVENT_GROUPS: { key: EventTypeMeta["group"]; label: string; color: string }[] = [
  { key: "rollover", label: "차량전복", color: "#d7263d" },
  { key: "danger", label: "위험운전", color: "#f28c28" },
  { key: "area", label: "이탈·위험지역", color: "#7c3aed" },
  { key: "etc", label: "기타", color: "#6b7280" },
];

export const RESULT_LABEL: Record<AnalysisEventPoint["result"], string> = {
  pending: "미처리",
  false: "사고 미발생 확인",
  "119": "119 출동",
  center: "센터 입고",
  to_rollover: "전복사고 전환",
  done: "처리 완료",
};

export const DURATION_BUCKETS: { key: string; label: string }[] = [
  { key: "m10", label: "~10분" },
  { key: "m30", label: "10~30분" },
  { key: "h1", label: "30분~1시간" },
  { key: "h2", label: "1~2시간" },
  { key: "h4", label: "2~4시간" },
  { key: "h8", label: "4~8시간" },
  { key: "h24", label: "8~24시간" },
  { key: "over", label: "24시간 초과" },
  { key: "odd", label: "시각 이상" },
  { key: "open", label: "종료 없음" },
];

/** 화면 요일 순서(월~일) → MariaDB DAYOFWEEK(1=일 ~ 7=토) */
export const WEEKDAYS: { label: string; dw: number }[] = [
  { label: "월", dw: 2 },
  { label: "화", dw: 3 },
  { label: "수", dw: 4 },
  { label: "목", dw: 5 },
  { label: "금", dw: 6 },
  { label: "토", dw: 7 },
  { label: "일", dw: 1 },
];

const pad = (x: number) => String(x).padStart(2, "0");
export const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** 기간 빠른 선택 */
export const PERIOD_PRESETS: { key: string; label: string; range: () => [string, string] }[] = [
  {
    key: "3m",
    label: "최근 3개월",
    range: () => {
      const e = new Date();
      const s = new Date(e);
      s.setMonth(s.getMonth() - 3);
      s.setDate(s.getDate() + 1);
      return [ymd(s), ymd(e)];
    },
  },
  {
    key: "6m",
    label: "최근 6개월",
    range: () => {
      const e = new Date();
      const s = new Date(e);
      s.setMonth(s.getMonth() - 6);
      s.setDate(s.getDate() + 1);
      return [ymd(s), ymd(e)];
    },
  },
  {
    key: "1y",
    label: "최근 1년",
    range: () => {
      const e = new Date();
      const s = new Date(e);
      s.setFullYear(s.getFullYear() - 1);
      s.setDate(s.getDate() + 1);
      return [ymd(s), ymd(e)];
    },
  },
  {
    key: "ytd",
    label: "올해",
    range: () => {
      const e = new Date();
      return [`${e.getFullYear()}-01-01`, ymd(e)];
    },
  },
  {
    key: "ly",
    label: "작년",
    range: () => {
      const y = new Date().getFullYear() - 1;
      return [`${y}-01-01`, `${y}-12-31`];
    },
  },
];

/** 값 목록을 5단계로 나누는 경계(분위수). 같은 값이 많으면 단계가 줄어든다 */
export function quantileBreaks(values: number[], classes = 5): number[] {
  const v = values.filter((x) => x > 0).sort((a, b) => a - b);
  if (!v.length) return [];
  const out: number[] = [];
  for (let i = 1; i < classes; i++) {
    const q = v[Math.min(v.length - 1, Math.floor((v.length * i) / classes))];
    if (!out.length || q > out[out.length - 1]) out.push(q);
  }
  return out;
}

/** 단계구분도 색 (연한 → 진한) */
export const CHORO_COLORS = ["#fee5d9", "#fcae91", "#fb6a4a", "#de2d26", "#a50f15"];

export const classOf = (value: number, breaks: number[]) => {
  let i = 0;
  while (i < breaks.length && value >= breaks[i]) i++;
  return i;
};
